import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { ObservationsResult } from '../../types/observation'
import { post } from '../../utils/api'

type DeleteRequest = {
  observationIds: Array<number>
  productId?: number
}

export const useObservationsDelete = (
  onObservationsDeleted?: (toAdd: number) => void
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  useEffect(() => {
    const handler = () => {
      // Celery task is done: refetch from page 1 to get accurate server state.
      // This also resets hasNextPage so infinite scroll works correctly after
      // a "Select All" delete that emptied all loaded pages (setting next: undefined).
      queryClient.invalidateQueries({
        queryKey: ['observations'],
        refetchType: 'active',
      })
      window.dispatchEvent(new CustomEvent('observations-changed'))
    }
    window.addEventListener('image-delete-completed', handler)
    return () => window.removeEventListener('image-delete-completed', handler)
  }, [queryClient])

  return useMutation(
    ['observations', 'delete'],
    (deleteRequest: DeleteRequest) =>
      post('/observations/bulk-destroy/', {
        product_id: deleteRequest.productId,
        observation_ids: deleteRequest.observationIds,
      }),
    {
      onSuccess: (_response: unknown, deleteRequest: DeleteRequest) => {
        const { observationIds } = deleteRequest
        const deletedSet = new Set(observationIds)

        // Optimistically remove deleted observations from all cached pages.
        queryClient.setQueriesData<InfiniteData<ObservationsResult>>(
          { queryKey: ['observations'] },
          (old) => {
            if (!old) return old

            const newPages = old.pages.map((page) => ({
              ...page,
              count: Math.max(
                0,
                page.count -
                  page.results.filter((o) => deletedSet.has(o.id)).length
              ),
              results: page.results.filter((o) => !deletedSet.has(o.id)),
            }))

            // If all loaded pages are now empty, null out `next` on the last
            // page so that hasNextPage becomes false. This prevents
            // InfiniteScroll from immediately firing fetchNextPage() on an
            // empty viewport, which would race with the server's async
            // bulk-delete (the API returns 200 before all rows are committed).
            const allPagesEmpty = newPages.every((p) => p.results.length === 0)
            if (allPagesEmpty && newPages.length > 0) {
              const lastIdx = newPages.length - 1
              return {
                ...old,
                pages: newPages.map((p, i) =>
                  i === lastIdx ? { ...p, next: undefined } : p
                ),
              }
            }

            return { ...old, pages: newPages }
          }
        )

        // Mark stale so the next mount/navigation triggers a clean server
        // refetch, but do not refetch immediately (avoids the same race).
        queryClient.invalidateQueries({
          queryKey: ['observations'],
          refetchType: 'none',
        })

        // Emit a custom event to notify other components about deleted observations
        // This is used by DatasetsList to refresh image counts from the backend
        window.dispatchEvent(
          new CustomEvent('observations-deleted', {
            detail: { deletedIds: observationIds },
          })
        )
        window.dispatchEvent(new CustomEvent('observations-changed'))

        onObservationsDeleted?.(observationIds.length * -1)
        toast.success(t('Observations deleted'))
      },
    }
  )
}
